import React, { useEffect, useMemo, useRef } from 'react'
import cx from 'clsx'
import { ControlledMenu, MenuButton, useClick, useMenuState } from '@szhsin/react-menu'
import { NavLink, generatePath, useLocation } from 'react-router-dom'
import { IconMenu2, IconSettings, IconX } from '@tabler/icons-react'

import OrganizationMenu from '@components/Navigation/OrganizationMenu'
import Lock from '@msteams/components/Navigation/components/Lock'
import { LinkGroups, getSingleLinks, LinkGroupTitles } from '@msteams/components/Navigation/utils'
import type { LinkGroupOption, SingleLinkObject } from '@msteams/components/Navigation/types'
import routes from '@msteams/Routes/routes'

import { useGetMyAccount } from '@queries/Account'
import { FeaturesToPaywall, useGetIsFeatureAvailable } from '@helpers/hooks/useGetHideWithPaywall'
import useGetIsAiChatbotEnabled from '@helpers/hooks/useGetIsAiChatbotEnabled'
import useGetIsAiAssistantHidden from '@helpers/hooks/useGetIsAiAssistantHidden'
import type { Feature } from '@type/features'

import styles from './index.module.css'

const SingleLink: React.FC<{ link: SingleLinkObject; onNavigate: () => void }> = ({ link, onNavigate }) => {
  const { pathname } = useLocation()
  const { available, paywallPath } = useGetIsFeatureAvailable(link.feature)
  const to = (!available && paywallPath ? paywallPath : link.to) || ''

  return (
    <NavLink to={to} onClick={onNavigate} className={cx(styles.navLink, link.isActive(pathname) && styles.active)}>
      <span className="flex-1 truncate">{link.label}</span>
      {!available && <Lock />}
    </NavLink>
  )
}

const GroupOptionLink: React.FC<{ option: LinkGroupOption; onNavigate: () => void }> = ({ option, onNavigate }) => {
  const { available, paywallPath } = useGetIsFeatureAvailable(option.feature)
  const to =
    !available && option.feature
      ? generatePath(routes.private.paywall, { feature: FeaturesToPaywall[option.feature as Feature] })
      : option.value

  // Delegate active-state to NavLink so detail routes (e.g. /bills/123) still
  // highlight the parent list link (e.g. /bills). `end={false}` is the default.
  return (
    <NavLink to={to} onClick={onNavigate} className={({ isActive }) => cx(styles.navLink, isActive && styles.active)}>
      <span className="flex-1 truncate">{option.label}</span>
      {!available && <Lock />}
    </NavLink>
  )
}

const TopBar: React.FC = () => {
  const { data: user } = useGetMyAccount()
  const aiChatbotEnabled = useGetIsAiChatbotEnabled()
  const aiAssistantHidden = useGetIsAiAssistantHidden()
  const perms = useMemo(() => new Set(user?.permissions ?? []), [user?.permissions])

  const anchorRef = useRef<Element>(null)
  const [menuState, toggleMenu] = useMenuState({ transition: { open: true } })
  const anchorProps = useClick(menuState.state, toggleMenu)
  const close = () => toggleMenu(false)

  const { pathname } = useLocation()

  // Close the drawer automatically when the route changes.
  useEffect(() => {
    if (menuState.state === 'open') toggleMenu(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const { settingsLink, primaryLinks } = useMemo(() => {
    const all = getSingleLinks(perms, aiChatbotEnabled, aiAssistantHidden)
    const settings = all.find((l) => l.to === routes.private.settings) || null
    const primary = all.filter((l) => l.to !== routes.private.settings)
    return { settingsLink: settings, primaryLinks: primary }
  }, [perms, aiChatbotEnabled, aiAssistantHidden])

  const payableOptions = useMemo(() => LinkGroups[0].getOptions(perms), [perms])
  const receivableOptions = useMemo(() => LinkGroups[1].getOptions(perms), [perms])

  return (
    <>
      <div className={styles.topBar}>
        <MenuButton
          ref={anchorRef}
          {...anchorProps}
          aria-label="Open navigation menu"
          className={styles.burgerButtonWrap}
        >
          <div className={styles.burgerButtonOverlay} />
          <IconMenu2 className="relative z-10 h-6 w-6 text-black" stroke={1.5} />
        </MenuButton>
        <OrganizationMenu
          align="start"
          direction="bottom"
          classes={{ menu: '!ml-0 !mt-1 w-[240px]', button: '!mx-0' }}
          forceClose={menuState.state === 'open' || menuState.state === 'opening'}
        />
      </div>

      <ControlledMenu {...menuState} anchorRef={anchorRef} onClose={close}>
        <div
          className={styles.drawer}
          style={{
            opacity: menuState.state === 'open' ? 1 : 0,
            bottom: menuState.state === 'open' ? 0 : '-100vh',
          }}
        >
          <div className="flex items-center justify-end px-3 h-10">
            <button
              type="button"
              aria-label="Close navigation menu"
              onClick={close}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
            >
              <IconX className="h-5 w-5 text-brand_primary" stroke={1.5} />
            </button>
          </div>

          <div className="px-2 flex flex-col gap-0.5">
            {primaryLinks.map((link) => (
              // Key by route (link.to) — ``primaryLinks`` is now
              // permission/feature-filtered so entries can insert or
              // drop while the drawer is mounted. Index-keyed items
              // would let React reuse the wrong SingleLink on filter
              // change.
              <SingleLink key={link.to} link={link} onNavigate={close} />
            ))}
          </div>

          {payableOptions.length > 0 && (
            <>
              <div className={styles.sectionTitle}>{LinkGroupTitles.payable}</div>
              <div className="px-2 flex flex-col gap-0.5">
                {payableOptions.map((option) => (
                  <GroupOptionLink key={option.value} option={option} onNavigate={close} />
                ))}
              </div>
            </>
          )}

          {receivableOptions.length > 0 && (
            <>
              <div className={styles.sectionTitle}>{LinkGroupTitles.receivable}</div>
              <div className="px-2 flex flex-col gap-0.5">
                {receivableOptions.map((option) => (
                  <GroupOptionLink key={option.value} option={option} onNavigate={close} />
                ))}
              </div>
            </>
          )}

          {settingsLink && (
            <div className="mt-3 pt-3 border-t border-gray-200 px-2">
              <NavLink
                to={settingsLink.to}
                onClick={close}
                className={cx(styles.navLink, settingsLink.isActive(pathname) && styles.active)}
              >
                <IconSettings className="h-5 w-5" stroke={1.5} />
                <span>Settings</span>
              </NavLink>
            </div>
          )}
        </div>
      </ControlledMenu>
    </>
  )
}

export default TopBar
